import React from 'react';
import { motion } from 'motion/react';
import { Search, ArrowRight, Video, Shield, LifeBuoy, BookOpen, MessageSquare, FileText, Bell, HelpCircle, Mail } from 'lucide-react';
import { GUIDE_CATEGORIES } from '@/data/guides';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DiscordCTA } from '@/components/DiscordCTA';

const HelpIndex = () => {
  return (
    <div className="space-y-16">
      {/* Hero */}
      <section className="text-center py-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Badge variant="outline" className="mb-6 rounded-full px-4 py-1 border-primary/20 text-primary uppercase tracking-[0.2em] text-[10px] bg-primary/5">
            Knowledge Base
          </Badge>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter mb-6">
            How can we help?
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-10">
            Search 100+ detailed guides covering every single setting, simulation mode, 
            and technical aspect of our platform.
          </p>
        </motion.div>
      </section>

      {/* Main Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {GUIDE_CATEGORIES.map((category, idx) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="group relative p-8 rounded-3xl bg-card border border-border hover:border-primary/50 transition-all duration-300"
          >
            <div className="absolute top-8 right-8 text-primary/10 group-hover:text-primary/20 transition-colors">
              <category.icon className="w-16 h-16" />
            </div>

            <div className="relative z-10">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <category.icon className="w-6 h-6" />
              </div>
              
              <h2 className="text-2xl font-bold mb-4 tracking-tight">{category.title}</h2>
              
              <ul className="space-y-3 mb-8">
                {category.articles.slice(0, 3).map(article => (
                  <li key={article.slug}>
                    <Link 
                      to={`/help/${category.id}/${article.slug}`}
                      className="text-muted-foreground hover:text-primary text-sm flex items-center gap-2 transition-colors"
                    >
                      <ArrowRight className="w-3 h-3 text-primary/40" />
                      {article.title}
                    </Link>
                  </li>
                ))}
              </ul>

              <Link 
                to={`/help/${category.id}/${category.articles[0].slug}`}
                className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-primary hover:gap-4 transition-all"
              >
                View all articles
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Support section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-10">
        <SupportCard 
          icon={<Video className="w-5 h-5" />}
          title="Video Tutorials"
          description="Watch our step-by-step video guides."
          link="/tutorials"
        />
        <SupportCard 
          icon={<MessageSquare className="w-5 h-5" />}
          title="Community Discord"
          description="Join thousands of creators."
          link="https://discord.gg/cdwNFdbx"
          isExternal
        />
        <SupportCard 
          icon={<Shield className="w-5 h-5" />}
          title="System Status"
          description="Check cloud rendering availability."
          link="/coming-soon"
        />
      </div>

      {/* Helpful Resources */}
      <section className="pt-6">
        <h3 className="text-lg font-bold font-display uppercase tracking-tight mb-6 text-foreground">Helpful Resources</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <ResourceLink icon={<BookOpen className="w-4 h-4" />} title="Creator Tutorials" description="Step-by-step guides for all skill levels" to="/tutorials" />
          <ResourceLink icon={<HelpCircle className="w-4 h-4" />} title="FAQ" description="100+ commonly asked questions answered" to="/faq" />
          <ResourceLink icon={<FileText className="w-4 h-4" />} title="Blog" description="Latest insights and creator strategies" to="/blog" />
          <ResourceLink icon={<Bell className="w-4 h-4" />} title="Updates" description="New features and engine improvements" to="/updates" />
          <ResourceLink icon={<Mail className="w-4 h-4" />} title="Contact Support" description="Get help from our team directly" to="/contact" />
          <a href="https://discord.gg/cdwNFdbx" target="_blank" rel="noopener noreferrer" className="p-4 rounded-2xl bg-[#5865F2]/5 border border-[#5865F2]/10 flex items-center gap-3 group hover:border-[#5865F2]/30 transition-all">
            <div className="w-8 h-8 rounded-xl bg-[#5865F2]/10 flex items-center justify-center text-[#5865F2] group-hover:bg-[#5865F2] group-hover:text-white transition-all">
              <MessageSquare className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-[#5865F2]">Discord Community</h4>
              <p className="text-xs text-muted-foreground">Beta testing, bugs & feature requests</p>
            </div>
          </a>
        </div>
      </section>

      <DiscordCTA variant="banner" />
    </div>
  );
};

const ResourceLink = ({ icon, title, description, to }: { icon: React.ReactNode; title: string; description: string; to: string }) => (
  <Link to={to} className="p-4 rounded-2xl bg-foreground/[0.02] border border-foreground/[0.05] flex items-center gap-3 group hover:border-primary/30 transition-all">
    <div className="w-8 h-8 rounded-xl bg-primary/5 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all">
      {icon}
    </div>
    <div>
      <h4 className="text-sm font-bold">{title}</h4>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  </Link>
);

const SupportCard = ({ icon, title, description, link, isExternal }: { icon: React.ReactNode; title: string; description: string; link: string; isExternal?: boolean }) => {
  const content = (
    <>
      <div className="w-10 h-10 rounded-xl bg-background border border-border flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all">
        {icon}
      </div>
      <div>
        <h4 className="text-sm font-bold">{title}</h4>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </>
  );

  if (isExternal) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" className="p-6 rounded-2xl bg-foreground/[0.02] border border-foreground/[0.05] flex items-center gap-4 group">
        {content}
      </a>
    );
  }

  return (
    <Link to={link} className="p-6 rounded-2xl bg-foreground/[0.02] border border-foreground/[0.05] flex items-center gap-4 group">
      {content}
    </Link>
  );
};

export default HelpIndex;
